/**
 * Domain model shared by server and clients.
 *
 * Vocabulary (mirrors broadcast intercom conventions):
 *  - Channel   : a partyline (many-to-many conference) or an IFB (interruptible foldback —
 *                listeners hear a program feed that is ducked when someone talks on it).
 *  - Panel     : a named key layout (which channels/users a person can talk/listen to).
 *                Users are assigned a panel, like "Director", "Camera", "Talent".
 *  - Key       : one tile on the panel. A key can target several channels at once (a "group key").
 *  - Target    : routing address string — `ch:<channelId>` or `user:<userId>` (direct/point-to-point).
 */

export type Role = 'admin' | 'user';

/** How the TALK control on a key behaves. */
export type TalkMode = 'momentary' | 'latch' | 'both' | 'none';

/** How the LISTEN control on a key behaves. */
export type ListenMode = 'toggle' | 'always' | 'none';

/**
 *  partyline : many-to-many conference.
 *  program   : a listen-only feed (mixer/program output) published by users allowed to feed program.
 *  ifb       : listeners hear the linked program channel, ducked whenever someone talks on the IFB.
 */
export type ChannelKind = 'partyline' | 'program' | 'ifb';

/** Named colour swatches — resolved to design-system tokens in the client. */
export type Swatch = 'cyan' | 'lime' | 'orange' | 'purple' | 'steel' | 'track' | 'red' | 'white';
export const SWATCHES: Swatch[] = ['cyan', 'lime', 'orange', 'purple', 'steel', 'track', 'red', 'white'];

export interface User {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  panelId: string | null;
  /** May publish a program audio feed into IFB channels (e.g. from a mixer output). */
  canFeedProgram: boolean;
  /**
   * Service account (Audio Injector, hardware bridge): never uses a seat of the plan's limit on
   * people online, and can listen and feed program audio but not talk from a mic.
   */
  service: boolean;
  color: Swatch;
  createdAt: number;
}

export interface Channel {
  id: string;
  name: string;
  /** 2–5 char label shown on hardware-style keys, e.g. "PROD", "IFB1". */
  short: string;
  kind: ChannelKind;
  color: Swatch;
  /** IFB only: how far program audio is ducked (in dB, negative) while someone talks. -100 = full cut. */
  dimDb: number;
  /** IFB only: the program channel whose feed this IFB carries. */
  programChannelId: string | null;
  order: number;
}

export interface KeyConfig {
  id: string;
  /** Override label; null = derive from targets. */
  label: string | null;
  /** One or more targets. Several targets make this a group key. */
  targets: string[];
  talk: TalkMode;
  listen: ListenMode;
  /** Initial listen state when the client connects (for `toggle` keys). */
  listenDefault: boolean;
  color: Swatch | null;
  /**
   * Voice-activated transmission: while the client's global VOX toggle is on, this key talks
   * automatically whenever the user's mic crosses the VOX threshold, with no PTT needed.
   * Purely a client-side behaviour — the server sees an ordinary talk.set call either way.
   */
  vox: boolean;
}

export interface Panel {
  id: string;
  name: string;
  keys: KeyConfig[];
}

export interface Presence {
  userId: string;
  online: boolean;
  /** Targets the user is currently talking to. */
  talking: string[];
  /** Targets the user is currently listening to. */
  listening: string[];
  /** Channel ids the user is currently feeding program audio into. */
  feeding: string[];
  /** Free-form client description (platform / browser). */
  client?: string;
}

export interface ChatMessage {
  id: string;
  from: string;
  /** Target the message was sent to (channel or direct user). */
  target: string;
  text: string;
  ts: number;
}

export interface ServerSettings {
  serverName: string;
}

/** Exported/imported configuration backup ("cloud configuration backup" equivalent). */
export interface ConfigBackup {
  version: 1;
  exportedAt: number;
  settings: ServerSettings;
  users: (User & { passwordHash: string })[];
  channels: Channel[];
  panels: Panel[];
}

// ---- Recording ---------------------------------------------------------------------------------

export type RecordingState = 'recording' | 'stopped';

/** One isolated speaker track within a channel recording (see server/src/recorder.ts). */
export interface RecordingTrack {
  userId: string;
  username: string;
  /** File path relative to the recordings directory. */
  file: string;
  startedAt: number;
  stoppedAt: number | null;
  bytes: number;
}

export interface Recording {
  id: string;
  channelId: string;
  channelName: string;
  state: RecordingState;
  startedBy: string;
  startedAt: number;
  stoppedAt: number | null;
  tracks: RecordingTrack[];
}
